import { useCallback, useEffect, useMemo, useState } from 'react';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { useUserRole } from '../context/UserRoleContext';
import { useDemoMode } from '../context/DemoModeContext';
import { canEditPropertyFrameworks } from '../constants/roles';
import {
  applyFrameworkVisibility,
  readFrameworkSelection,
  resolveFrameworks,
} from '../constants/disclosure';
import {
  hydrateDisclosure,
  loadDisc,
  loadLifts,
  saveFrameworkSelection,
  saveFrameworkSuggestion,
  subscribeDisclosureSelection,
} from '../api/disclosure';

/**
 * Per-property disclosure framework selection + visibility.
 * Hydrates the client-held disclosureInputs doc (same cache as the Disclosure
 * page) so Overview / Assessments / the tab marker share one choice.
 */
export default function useDisclosureFrameworks(farmId, published) {
  const { role, farmIds, uid } = useUserRole();
  const demoMode = useDemoMode();
  const [ownerUid, setOwnerUid] = useState(null);
  const [hydrated, setHydrated] = useState(false);
  const [tick, setTick] = useState(0);

  useEffect(() => {
    if (!farmId) {
      setHydrated(true);
      return undefined;
    }
    let alive = true;
    setHydrated(false);
    (async () => {
      try {
        await hydrateDisclosure(farmId);
        const snap = await getDoc(doc(db, 'farms', farmId));
        if (alive && snap.exists()) {
          setOwnerUid(snap.data()?.ownerUid || null);
        } else if (alive) {
          setOwnerUid(null);
        }
      } catch (err) {
        console.error('[useDisclosureFrameworks] hydrate failed', err);
      } finally {
        if (alive) {
          setHydrated(true);
          setTick((n) => n + 1);
        }
      }
    })();
    return () => { alive = false; };
  }, [farmId]);

  // Same-tab: the chooser lives on Disclosure, the tab marker lives in
  // DashTopNav. A save must refresh every hook instance, not only the saver.
  useEffect(() => subscribeDisclosureSelection((changedFarmId) => {
    if (changedFarmId && farmId && changedFarmId !== farmId) return;
    setTick((n) => n + 1);
  }), [farmId]);

  const selection = useMemo(
    () => readFrameworkSelection(loadDisc(farmId)),
    // tick busts the sync localStorage read after hydrate / save
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [farmId, tick],
  );

  const canEdit = useMemo(() => {
    if (demoMode?.isDemoModeEnabled) return false;
    return canEditPropertyFrameworks({ role, uid, farmIds, ownerUid, farmId });
  }, [demoMode?.isDemoModeEnabled, role, uid, farmIds, ownerUid, farmId]);

  const allRows = useMemo(
    () => resolveFrameworks(published, loadLifts(farmId)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [published, farmId, tick],
  );

  const visibility = useMemo(
    () => applyFrameworkVisibility(allRows, selection, canEdit),
    [allRows, selection, canEdit],
  );

  const refresh = useCallback(() => setTick((n) => n + 1), []);

  const saveEnabled = useCallback(async (enabledKeys) => {
    await saveFrameworkSelection(farmId, enabledKeys);
    setTick((n) => n + 1);
  }, [farmId]);

  const suggestFramework = useCallback(async ({ name, note }) => {
    const entry = await saveFrameworkSuggestion(farmId, { name, note, uid });
    setTick((n) => n + 1);
    return entry;
  }, [farmId, uid]);

  return {
    hydrated,
    selection,
    canEdit,
    allRows,
    visibility,
    needsTabMarker: !!(farmId && canEdit && !selection.choiceMade),
    refresh,
    saveEnabled,
    suggestFramework,
  };
}
